// ============================================
// Chromatic Aberration Post-Process Effect
// ============================================
// Production-quality spectral dispersion with:
// - 6-band spectral sampling
// - Multiple distortion modes
// - Edge falloff control
// - Anamorphic stretch

import type { IRenderContext } from '../../core/IRenderContext';
import { 
  PostProcessEffect, 
  type PostProcessContext, 
  type PostProcessParameter 
} from '../PostProcessStack';
import { CHROMATIC_ABERRATION_LAYOUT } from '../PostProcessLayouts';
import {
  type PipelineHandle,
  type ShaderHandle,
  NULL_HANDLE,
} from '../../types';
import { getCompiledShader } from '../ShaderCache';

/**
 * Distortion mode for chromatic aberration
 */
export enum ChromaticMode {
  Radial = 0,      // Standard radial dispersion
  Barrel = 1,      // Barrel distortion (fish-eye like)
  Pincushion = 2,  // Pincushion distortion
}

/**
 * Chromatic Aberration post-processing effect
 * Simulates lens imperfection by separating color channels
 * with physically-based spectral dispersion
 */
export class ChromaticAberrationEffect extends PostProcessEffect {
  readonly type = 'chromaticAberration';
  readonly displayName = 'Chromatic Aberration';
  
  // Core parameters
  aberrationIntensity = 1.0;   // Aberration strength
  falloff = 2.0;               // Edge falloff (1=linear, 2=quadratic)
  
  // Quality
  samples = 6;                 // Spectral samples (3-8)
  
  // Mode
  mode = ChromaticMode.Radial; // Distortion type
  
  // Center & shape
  centerX = 0.5;
  centerY = 0.5;
  anamorphicX = 1.0;           // Horizontal stretch
  anamorphicY = 1.0;           // Vertical stretch
  
  // GPU resources
  private shader: ShaderHandle = NULL_HANDLE;
  private pipelineHDR: PipelineHandle = NULL_HANDLE;
  private pipelineLDR: PipelineHandle = NULL_HANDLE;
  private uniformWriter = CHROMATIC_ABERRATION_LAYOUT.createWriter();
  
  getParameters(): PostProcessParameter[] {
    return [
      { name: 'aberrationIntensity', type: 'float', min: 0, max: 5, default: 1.0, description: 'Aberration strength' },
      { name: 'falloff', type: 'float', min: 0.5, max: 4, default: 2.0, description: 'Edge falloff power' },
      { name: 'samples', type: 'int', min: 3, max: 8, default: 6, description: 'Spectral samples' },
      { name: 'mode', type: 'int', min: 0, max: 2, default: 0, description: 'Mode (0=radial, 1=barrel, 2=pinch)' },
      { name: 'centerX', type: 'float', min: 0, max: 1, default: 0.5, description: 'Center X' },
      { name: 'centerY', type: 'float', min: 0, max: 1, default: 0.5, description: 'Center Y' },
      { name: 'anamorphicX', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Horizontal stretch' },
      { name: 'anamorphicY', type: 'float', min: 0.1, max: 3, default: 1.0, description: 'Vertical stretch' },
    ];
  }
  
  getParameterValue(name: string): number | boolean | number[] {
    switch (name) {
      case 'aberrationIntensity': return this.aberrationIntensity;
      case 'falloff': return this.falloff;
      case 'samples': return this.samples;
      case 'mode': return this.mode;
      case 'centerX': return this.centerX;
      case 'centerY': return this.centerY;
      case 'anamorphicX': return this.anamorphicX;
      case 'anamorphicY': return this.anamorphicY;
      default: return 0;
    }
  }
  
  setParameterValue(name: string, value: number | boolean | number[]): void {
    if (typeof value !== 'number') return;
    switch (name) {
      case 'aberrationIntensity': this.aberrationIntensity = Math.max(0, value); break;
      case 'falloff': this.falloff = Math.max(0.5, Math.min(4, value)); break;
      case 'samples': this.samples = Math.floor(Math.max(3, Math.min(8, value))); break;
      case 'mode': this.mode = Math.floor(Math.max(0, Math.min(2, value))) as ChromaticMode; break;
      case 'centerX': this.centerX = Math.max(0, Math.min(1, value)); break;
      case 'centerY': this.centerY = Math.max(0, Math.min(1, value)); break;
      case 'anamorphicX': this.anamorphicX = Math.max(0.1, Math.min(3, value)); break;
      case 'anamorphicY': this.anamorphicY = Math.max(0.1, Math.min(3, value)); break;
    }
  }
  
  protected onInitialize(ctx: IRenderContext): void {
    const hdrFormat = 'rgba16float' as const;
    const ldrFormat = ctx.getSwapChainFormat();
    
    const chromaticWgsl = getCompiledShader('postprocess/chromatic-aberration');
    this.shader = ctx.createShader(chromaticWgsl, chromaticWgsl);
    
    this.pipelineHDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: hdrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'chromatic-aberration-hdr',
    });
    this.pipelineLDR = ctx.createPipeline({
      shader: this.shader,
      vertexBuffers: [],
      colorTargets: [{ format: ldrFormat }],
      primitive: { topology: 'triangle-list', cullMode: 'none', usesTextures: true, textureCount: 1 },
      label: 'chromatic-aberration-ldr',
    });
  }
  
  protected onRender(context: PostProcessContext): void {
    const { ctx, source, width, height, intensity, isHdrTarget } = context;
    
    // NOTE: PostProcessStack already set render target and viewport

    this.uniformWriter.setAll({
      intensity: this.aberrationIntensity * intensity,
      falloff: this.falloff,
      samples: this.samples,
      mode: this.mode,
      center: [this.centerX, this.centerY],
      anamorphic: [this.anamorphicX, this.anamorphicY],
    });
    this.fullscreenPass(ctx, isHdrTarget ? this.pipelineHDR : this.pipelineLDR, this.uniformWriter.float32, [source]);
  }
  
  protected onDispose(ctx: IRenderContext): void {
    if (this.pipelineHDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineHDR);
    if (this.pipelineLDR !== NULL_HANDLE) ctx.destroyPipeline(this.pipelineLDR);
    if (this.shader !== NULL_HANDLE) ctx.destroyShader(this.shader);
    this.pipelineHDR = NULL_HANDLE;
    this.pipelineLDR = NULL_HANDLE;
    this.shader = NULL_HANDLE;
  }
}
